'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { PromotionReplyOrder } from '@diritto/common/promotion/constants';
import {
  GameWeeklyNumber,
  getGameWeeklyNumberByItemKey,
  getLatestGameWeeklyNumber,
} from '@diritto/common/promotion/2025-balance-ritto.constants';
import { PromotionDto, PromotionItemDto } from '@diritto/common/promotion/dtos/promotion.dto';
import { ContentBodyNoPaddingContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { PromotionReplyTab } from 'app-components/promotions/PromotionReplyTab';
import { PromotionReplyInputView } from 'app-components/promotions/PromotionReplyInput';
import { useGetPromotionParticipatesByUserQuery } from 'queries/promotions/useGetPromotionParticipatesByUserQuery';
import { useCreatePromotionReplyMutation } from 'queries/promotions/mutations/useCreatePromotionReplyMutation';
import { useAuthState } from 'contexts/AuthProvider';
import { ModalHandler } from 'utils/ModalHandler';
import { ModalType } from 'constants/modal';
import { buildSignInUrl } from 'utils/url';
import { promotionsQueryKey } from 'queries/promotions/queryKey';
import { PromotionReplyList } from 'app-components/promotions/PromotionReplyList';

type PromotionReplyListViewProps = {
  week: GameWeeklyNumber;
  orderBy: PromotionReplyOrder;
  page: number;
};

const defaultParams: PromotionReplyListViewProps = {
  week: 1,
  page: 1,
  orderBy: PromotionReplyOrder.Latest,
};

export const PromotionReplyListView = ({
  promotion,
  promotionId,
  promotionItems,
  activeWeeks,
}: {
  promotion?: PromotionDto;
  promotionId: number;
  promotionItems: Record<string, PromotionItemDto>;
  activeWeeks: GameWeeklyNumber[];
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { user, profile } = useAuthState();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  const initialWeek = searchParams?.get('week');
  const initialPage = searchParams?.get('page');
  const initialOrderBy = searchParams?.get('orderBy');

  const getInitialParams = useCallback(
    (): PromotionReplyListViewProps => ({
      week: initialWeek
        ? (Number(initialWeek) as GameWeeklyNumber)
        : (getLatestGameWeeklyNumber(activeWeeks) ?? defaultParams.week),
      page: initialPage ? Number(initialPage) : defaultParams.page,
      orderBy: initialOrderBy ? (initialOrderBy as PromotionReplyOrder) : defaultParams.orderBy,
    }),
    [initialWeek, initialPage, initialOrderBy, activeWeeks],
  );

  const [params, setParams] = useState<PromotionReplyListViewProps>(getInitialParams);

  const [weeklyCount, setWeeklyCount] = useState<number>(0);

  const { mutate: createMutate } = useCreatePromotionReplyMutation(promotionId);
  const { data: participateByUserData } = useGetPromotionParticipatesByUserQuery(promotionId, !!user);

  const selectedAnswers = useMemo(() => participateByUserData?.participateByWeek, [participateByUserData]);
  const getWeekAnswer = useCallback(
    (answeredWeek: GameWeeklyNumber) => selectedAnswers?.[answeredWeek],
    [selectedAnswers],
  );

  useEffect(() => {
    setParams(getInitialParams());
  }, [getInitialParams]);

  const promotionItemId = useMemo(() => {
    const item = Object.values(promotionItems ?? {}).find(({ key }) => {
      const week = getGameWeeklyNumberByItemKey(key);
      return week === params.week;
    });
    return item?.id;
  }, [promotionItems, params.week]);

  const handleInvalidQuery = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: promotionsQueryKey.promotionReplies,
      exact: false,
    });
  }, [queryClient]);

  const handleParamsChange = useCallback(
    (updatedParams: Partial<PromotionReplyListViewProps>) => {
      const mergedParams = {
        ...params,
        ...updatedParams,
      };
      router.replace(
        `${pathname}?${new URLSearchParams(mergedParams as unknown as Record<string, string>).toString()}#reply`,
      );
    },
    [params, pathname, router],
  );

  const handleChangeOrderBy = useCallback(
    (orderBy: PromotionReplyOrder) => {
      handleParamsChange({ orderBy, page: 1 });
      handleInvalidQuery();
    },
    [handleParamsChange, handleInvalidQuery],
  );

  const handleChangeWeek = useCallback(
    (week: GameWeeklyNumber) => {
      handleParamsChange({ week, page: 1 });
      setWeeklyCount(0);
      handleInvalidQuery();
    },
    [handleParamsChange, handleInvalidQuery, setWeeklyCount],
  );

  const handleChangePage = useCallback(
    (page: number) => {
      handleParamsChange({ page });
    },
    [handleParamsChange],
  );

  const handleCreateReply = useCallback(
    (value: string, callback?: (success?: boolean, errorCode?: string) => void) => {
      if (!profile) {
        ModalHandler.show(ModalType.Alert, {
          title: '로그인 후 이용해주세요.',
          confirmText: '로그인',
          cancelText: '닫기',
          onConfirm: () => router.push(buildSignInUrl()),
        });
        return;
      }

      if (promotion?.endsAt && DateTime.fromISO(promotion.endsAt) < DateTime.local({ zone: 'Asia/Seoul' })) {
        ModalHandler.show(ModalType.Alert, {
          title: '프로모션이 종료되었습니다.',
        });
        return;
      }

      createMutate(
        { description: value },
        {
          onSuccess: () => {
            callback?.(true);
            const changedParams = {
              page: 1,
              week: getLatestGameWeeklyNumber(activeWeeks) ?? defaultParams.week,
              orderBy: PromotionReplyOrder.Latest,
            };
            handleParamsChange(changedParams);
            // TODO: 캐시 무효화 처리 최적화 필요.
            handleInvalidQuery();
          },
          onError: (error: any) => callback?.(false, error?.response?.data?.code),
        },
      );
    },
    [createMutate, profile, router, handleParamsChange, handleInvalidQuery, activeWeeks, promotion?.endsAt],
  );

  return (
    <ContentBodyNoPaddingContainer ref={ref} className="flex flex-col gap-0">
      <PromotionReplyInputView
        onSend={handleCreateReply}
        isAnswered={
          !!getWeekAnswer(getLatestGameWeeklyNumber(activeWeeks) ?? 1) &&
          profile?.id === participateByUserData?.profileId
        }
        maxLength={1000}
      />
      <PromotionReplyTab
        className="mb-4"
        orderBy={params.orderBy}
        orderList={[PromotionReplyOrder.Latest, PromotionReplyOrder.Like]}
        onChangeOrderBy={handleChangeOrderBy}
        week={params.week}
        weeklyList={activeWeeks}
        weeklyCount={weeklyCount}
        onChangeWeek={handleChangeWeek}
      />
      <PromotionReplyList
        promotionId={promotionId}
        orderBy={params.orderBy}
        promotionItemId={promotionItemId}
        currentPage={params.page}
        onPageChange={handleChangePage}
        onWeeklyCountChange={setWeeklyCount}
      />
    </ContentBodyNoPaddingContainer>
  );
};
